import { chooseInitialTenant, readActiveTenant, writeActiveTenant } from "./active-tenant.js";
import { createAmplifyAuthApi } from "./amplify-api.js";
import { homeTenantOf, platformPrincipalFromClaims, tenantPrincipalFromClaims, withActiveTenant, } from "./principal.js";
import { AuthFlowError } from "./types.js";
/**
 * Cognito over USER_SRP_AUTH through `aws-amplify/auth` v6 (ADR *cognito-srp-via-amplify-auth*):
 * the password is turned into an SRP proof in the browser and never travels in clear; tokens
 * live in this adapter's own Amplify context (no global singleton, no localStorage); sign-out
 * revokes the refresh token. Every challenge Cognito can raise for this pool configuration
 * (new password on a temporary one, TOTP code, TOTP enrolment, an unconfirmed self sign-up)
 * becomes an `AuthStep`. A personal product's pool also takes self sign-up (`signUp` →
 * `confirmSignUp` → SRP sign-in, ADR *personal-tenants-sign-up-in-the-cell-pool*).
 */
export function createCognitoAuth(options) {
    const api = options.api ?? createAmplifyAuthApi(options.config, options.storage ?? 'memory');
    let current = null;
    let pending = null;
    /** Kept only while a `totp-setup` challenge is open, so the QR is built once. */
    let setup = null;
    const claimsOf = (session) => {
        const payload = session.tokens?.idToken?.payload;
        return payload ?? null;
    };
    const principalFromSession = (session, tenantHint) => {
        const claims = claimsOf(session);
        if (!claims)
            throw new AuthFlowError('no-session', 'Not signed in');
        if (options.audience === 'platform')
            return platformPrincipalFromClaims(claims);
        const base = tenantPrincipalFromClaims(claims);
        const tenantId = chooseInitialTenant(base.workspaces.map((w) => w.tenantId), { tenantHint, remembered: readActiveTenant(), home: homeTenantOf(claims, base) });
        return tenantId ? withActiveTenant(base, tenantId) : base;
    };
    const adopt = (session, tenantHint) => {
        current = principalFromSession(session, tenantHint);
        if (current.tenantId && options.audience === 'tenant')
            writeActiveTenant(current.tenantId);
        return current;
    };
    const finish = async (tenantHint) => {
        const principal = adopt(await api.fetchAuthSession(), tenantHint);
        pending = null;
        setup = null;
        return { kind: 'done', principal };
    };
    const stepFrom = async (output, email, tenantHint) => {
        const step = output.nextStep;
        const remember = (challenge) => {
            pending = { challenge, email, ...(tenantHint ? { tenantHint } : {}) };
        };
        switch (step.signInStep) {
            case 'DONE':
                return finish(tenantHint);
            case 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED':
                remember('new-password');
                return { kind: 'new-password-required' };
            case 'CONFIRM_SIGN_IN_WITH_TOTP_CODE':
                remember('totp-code');
                return { kind: 'totp-code' };
            case 'CONTINUE_SIGN_IN_WITH_TOTP_SETUP': {
                remember('totp-setup');
                setup = {
                    secret: step.totpSetupDetails.sharedSecret,
                    uri: step.totpSetupDetails.getSetupUri(options.appName, email).toString(),
                };
                return { kind: 'totp-setup', ...setup };
            }
            case 'CONTINUE_SIGN_IN_WITH_MFA_SELECTION':
            case 'CONTINUE_SIGN_IN_WITH_MFA_SETUP_SELECTION': {
                // SMS is off on every Sprocket pool; TOTP is the only answer.
                const next = await wrap(() => api.confirmSignIn({ challengeResponse: 'TOTP' }));
                return stepFrom(next, email, tenantHint);
            }
            case 'CONFIRM_SIGN_UP':
                // A self sign-up whose emailed code was never entered (the SDK answers this as a step).
                return { kind: 'confirm-sign-up' };
            case 'RESET_PASSWORD':
                throw new AuthFlowError('not-authorized', 'Your password must be reset before you can sign in. Use "Forgot password".');
            default:
                // Never the step's name: it is the SDK's vocabulary, not the person's.
                throw new AuthFlowError('unknown', 'Sign-in could not be completed. Try again.');
        }
    };
    const needPending = (challenge) => {
        if (!pending || pending.challenge !== challenge)
            throw new AuthFlowError('no-session', 'Start by signing in again.');
        return pending;
    };
    return {
        mode: 'cognito',
        fixtures: () => [],
        signInAsFixture: () => Promise.reject(new AuthFlowError('unknown', 'Fixtures exist in mock mode only')),
        restore: async () => {
            try {
                const session = await api.fetchAuthSession();
                if (!claimsOf(session)) {
                    current = null;
                    return null;
                }
                current = principalFromSession(session);
                return current;
            }
            catch {
                current = null;
                return null;
            }
        },
        signIn: async ({ email, password, tenantHint }) => {
            pending = null;
            const output = await wrap(() => api.signIn({ username: email, password, options: { authFlowType: 'USER_SRP_AUTH' } }));
            return stepFrom(output, email, tenantHint);
        },
        completeNewPassword: async (newPassword) => {
            const p = needPending('new-password');
            const output = await wrap(() => api.confirmSignIn({ challengeResponse: newPassword }));
            return stepFrom(output, p.email, p.tenantHint);
        },
        confirmTotp: async (code) => {
            const p = needPending('totp-code');
            const output = await wrap(() => api.confirmSignIn({ challengeResponse: code.trim() }));
            return stepFrom(output, p.email, p.tenantHint);
        },
        beginTotpSetup: async () => {
            if (pending?.challenge === 'totp-setup' && setup)
                return setup;
            if (!current)
                throw new AuthFlowError('no-session', 'Sign in first');
            const details = await wrap(() => api.setUpTOTP());
            return {
                secret: details.sharedSecret,
                uri: details.getSetupUri(options.appName, current.email).toString(),
            };
        },
        confirmTotpSetup: async (code) => {
            if (pending?.challenge === 'totp-setup') {
                const p = pending;
                const output = await wrap(() => api.confirmSignIn({ challengeResponse: code.trim() }));
                return stepFrom(output, p.email, p.tenantHint);
            }
            if (!current)
                throw new AuthFlowError('no-session', 'Sign in first');
            await wrap(() => api.verifyTOTPSetup({ code: code.trim() }));
            await wrap(() => api.updateMFAPreference({ totp: 'PREFERRED' }));
            return { kind: 'done', principal: current };
        },
        forgotPassword: async (email) => {
            const output = await wrap(() => api.resetPassword({ username: email }));
            const step = output.nextStep;
            return {
                destination: step.resetPasswordStep === 'CONFIRM_RESET_PASSWORD_WITH_CODE'
                    ? (step.codeDeliveryDetails.destination ?? null)
                    : null,
            };
        },
        resetPassword: ({ email, code, newPassword }) => wrap(() => api.confirmResetPassword({ username: email, confirmationCode: code.trim(), newPassword }), 'email-code'),
        signUp: async ({ email, password, givenName, familyName }) => {
            // Only the standard attributes: the custom claims are identity's to write, never the person's.
            const userAttributes = { email };
            if (givenName?.trim())
                userAttributes['given_name'] = givenName.trim();
            if (familyName?.trim())
                userAttributes['family_name'] = familyName.trim();
            const output = await wrap(() => api.signUp({ username: email, password, options: { userAttributes } }), 'sign-up');
            const step = output.nextStep;
            return step.signUpStep === 'CONFIRM_SIGN_UP'
                ? { kind: 'confirm', destination: step.codeDeliveryDetails.destination ?? null }
                : { kind: 'done' };
        },
        confirmSignUp: async ({ email, code }) => {
            await wrap(() => api.confirmSignUp({ username: email, confirmationCode: code.trim() }), 'email-code');
        },
        resendSignUpCode: async (email) => {
            const details = await wrap(() => api.resendSignUpCode({ username: email }), 'email-code');
            return { destination: details.destination ?? null };
        },
        refresh: async () => {
            // New tokens from the refresh token: claims identity rewrote server-side (a membership
            // added by provisioning) reach the principal without a second sign-in.
            let session;
            try {
                session = await api.fetchAuthSession({ forceRefresh: true });
            }
            catch (e) {
                // A revoked or expired refresh token is "no session", not a wrong password.
                const name = e instanceof Error ? e.name : '';
                if (name === 'NotAuthorizedException' || name === 'UserUnAuthenticatedException') {
                    current = null;
                    return null;
                }
                throw new AuthFlowError('unknown', 'We could not refresh your session. Check your connection and try again.', e);
            }
            if (!claimsOf(session)) {
                current = null;
                return null;
            }
            return adopt(session);
        },
        changePassword: ({ currentPassword, newPassword }) => wrap(() => api.updatePassword({ oldPassword: currentPassword, newPassword })),
        signOut: async () => {
            current = null;
            pending = null;
            setup = null;
            writeActiveTenant(null);
            try {
                await api.signOut(); // revokes the refresh token, then clears the token store
            }
            catch {
                // Offline or already revoked: the local session is gone either way.
            }
        },
        switchTenant: (tenantId) => {
            if (!current)
                return Promise.reject(new AuthFlowError('no-session', 'Not signed in'));
            if (options.audience !== 'tenant') {
                return Promise.reject(new AuthFlowError('not-a-member', 'Platform staff do not switch workspaces'));
            }
            try {
                current = withActiveTenant(current, tenantId);
            }
            catch {
                return Promise.reject(new AuthFlowError('not-a-member', 'You are not a member of that workspace'));
            }
            writeActiveTenant(tenantId);
            return Promise.resolve(current);
        },
        getAccessToken: async () => {
            try {
                const session = await api.fetchAuthSession();
                return session.tokens?.idToken?.toString() ?? null;
            }
            catch {
                return null;
            }
        },
    };
}
/** Run one SDK call, turning Amplify/Cognito errors into `AuthFlowError`s. */
async function wrap(call, flow = 'sign-in') {
    try {
        return await call();
    }
    catch (e) {
        throw toAuthFlowError(e, flow);
    }
}
const POLICY_MESSAGE = 'Use at least 12 characters with upper and lower case, a number and a symbol.';
const TOO_MANY = 'Too many attempts. Wait a few minutes and try again.';
const ALREADY_CONFIRMED = 'This email is already confirmed. Sign in to continue.';
/**
 * Cognito exception names → a stable code and a sentence for people. Never a vendor name, and
 * never the exception's own message: it is written for developers.
 */
export function toAuthFlowError(e, flow = 'sign-in') {
    if (e instanceof AuthFlowError)
        return e;
    const name = e instanceof Error ? e.name : '';
    const message = e instanceof Error ? e.message : '';
    if (flow === 'sign-up' && name === 'NotAuthorizedException')
        return new AuthFlowError('not-authorized', 'New accounts cannot be created here.', e);
    if (flow === 'email-code') {
        // ConfirmSignUp on a confirmed account: "User cannot be confirmed. Current status is CONFIRMED";
        // ResendConfirmationCode: an InvalidParameterException "User is already confirmed."
        if ((name === 'NotAuthorizedException' || name === 'InvalidParameterException') &&
            /confirmed/i.test(message))
            return new AuthFlowError('already-confirmed', ALREADY_CONFIRMED, e);
        if (name === 'CodeMismatchException')
            return new AuthFlowError('invalid-code', 'That code is not right. Check the email and try again.', e);
    }
    switch (name) {
        case 'UsernameExistsException':
        case 'AliasExistsException':
            return new AuthFlowError('account-exists', 'An account with this email already exists. Sign in instead.', e);
        case 'CodeDeliveryFailureException':
            return new AuthFlowError('unknown', 'We could not send the code. Wait a few minutes and try again.', e);
        case 'NotAuthorizedException':
        case 'UserNotFoundException':
            if (/password attempts exceeded/i.test(message))
                return new AuthFlowError('too-many-attempts', TOO_MANY, e);
            if (/session is expired|invalid session/i.test(message))
                return new AuthFlowError('no-session', 'That step took too long. Sign in again.', e);
            return new AuthFlowError('invalid-credentials', 'The email or password is not right.', e);
        case 'CodeMismatchException':
        case 'EnableSoftwareTokenMFAException':
            return new AuthFlowError('invalid-code', 'That code is not right. Check the app and try again.', e);
        case 'ExpiredCodeException':
            return new AuthFlowError('expired-code', 'That code has expired. Request a new one.', e);
        case 'InvalidPasswordException':
            return new AuthFlowError('password-policy', POLICY_MESSAGE, e);
        case 'LimitExceededException':
        case 'TooManyRequestsException':
        case 'TooManyFailedAttemptsException':
            return new AuthFlowError('too-many-attempts', TOO_MANY, e);
        case 'PasswordResetRequiredException':
            return new AuthFlowError('not-authorized', 'Your password must be reset. Use "Forgot password".', e);
        case 'UserNotConfirmedException':
            return new AuthFlowError('not-confirmed', 'This account is not ready yet. Confirm your email with the code we sent, then sign in.', e);
        case 'InvalidParameterException':
            return new AuthFlowError('unknown', 'Check the details you entered and try again.', e);
        default:
            return new AuthFlowError('unknown', 'Something went wrong. Try again.', e);
    }
}
//# sourceMappingURL=cognito-auth.js.map